import { GRADE_OPTIONS, birthdayFromId } from "./data";
import { formatPhoneInput, phoneDigits } from "./phone";
import {
  GENDERS,
  HELP_INTERESTS,
  RACES,
  RELATIONSHIPS,
  SCHOOL_FORM_ORDER,
  emptyPermission,
  type GenderId,
  type HelpInterestId,
  type PermissionAnswers,
  type RaceId,
  type YesNo,
} from "./permissionForm";
import {
  joinCheckboxAnswers,
  type FormQuestion,
} from "./questions";

export type SampleStep = "student" | "pickup" | "health" | "policies";

export type SampleBeat = {
  step: SampleStep;
  fieldId: string;
  answers: PermissionAnswers;
};

export type SampleGuardian = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
};

const GIVEN = [
  "Avery",
  "Blake",
  "Casey",
  "Drew",
  "Ellis",
  "Finley",
  "Gray",
  "Harper",
  "Indigo",
  "Jules",
  "Kai",
  "Logan",
  "Marlow",
  "Nico",
  "Oakley",
  "Parker",
  "Quinn",
  "Reese",
  "Sawyer",
];

const FAMILY = [
  "Adler",
  "Brooks",
  "Callahan",
  "Dalton",
  "Ellison",
  "Foster",
  "Griffin",
  "Hayes",
  "Ingram",
  "Jensen",
  "Keene",
  "Lang",
  "Mercer",
  "Nolan",
  "Osborne",
  "Prescott",
  "Quincy",
  "Rowe",
  "Walsh",
];

const TOWNS = ["Suttons Bay", "Leland", "Northport", "Maple City"];

type Slot = {
  step: SampleStep;
  fieldId: string;
  apply: (complete: PermissionAnswers, current: PermissionAnswers) => PermissionAnswers;
  when?: (complete: PermissionAnswers) => boolean;
};

const FILL_SLOTS: Slot[] = [
  slot("student", "student-first", (complete, current) =>
    withStudent(current, { firstName: complete.student.firstName }),
  ),
  slot("student", "student-last", (complete, current) =>
    withStudent(current, { lastName: complete.student.lastName }),
  ),
  slot("student", "student-email", (complete, current) =>
    withStudent(current, { email: complete.student.email }),
  ),
  slot("student", "student-grade", (complete, current) =>
    withStudent(current, { grade: complete.student.grade }),
  ),
  slot("student", "student-birthday", (complete, current) =>
    withStudent(current, { birthday: complete.student.birthday }),
  ),
  slot("student", "student-phone", (complete, current) =>
    withStudent(current, { phone: complete.student.phone }),
  ),
  slot("student", "student-address", (complete, current) =>
    withStudent(current, { address: complete.student.address }),
  ),
  slot("student", "student-gender", (complete, current) =>
    withStudent(current, { gender: complete.student.gender }),
  ),
  slot("student", "student-race", (complete, current) =>
    withStudent(current, { races: [...complete.student.races] }),
  ),
  slot("student", "student-school", (complete, current) =>
    withStudent(current, { school: complete.student.school }),
  ),
  slot("student", "guardian-first", (complete, current) =>
    withGuardian(current, { firstName: complete.guardian.firstName }),
  ),
  slot("student", "guardian-last", (complete, current) =>
    withGuardian(current, { lastName: complete.guardian.lastName }),
  ),
  slot("student", "guardian-email", (complete, current) =>
    withGuardian(current, { email: complete.guardian.email }),
  ),
  slot("student", "guardian-phone", (complete, current) =>
    withGuardian(current, { phone: complete.guardian.phone }),
  ),
  slot("student", "guardian-relationship", (complete, current) =>
    withGuardian(current, { relationship: complete.guardian.relationship }),
  ),
  slot("student", "guardian-address", (complete, current) =>
    withGuardian(current, { address: complete.guardian.address }),
  ),
  slot("student", "guardian-help", (complete, current) =>
    withGuardian(current, { help: [...complete.guardian.help] }),
  ),
  slot("student", "additional-first", (complete, current) =>
    withAdditional(current, { firstName: complete.additionalGuardian.firstName }),
  ),
  slot("student", "additional-last", (complete, current) =>
    withAdditional(current, { lastName: complete.additionalGuardian.lastName }),
  ),
  slot("student", "additional-email", (complete, current) =>
    withAdditional(current, { email: complete.additionalGuardian.email }),
  ),
  slot("student", "additional-phone", (complete, current) =>
    withAdditional(current, { phone: complete.additionalGuardian.phone }),
  ),
  slot("student", "additional-relationship", (complete, current) =>
    withAdditional(current, { relationship: complete.additionalGuardian.relationship }),
  ),
  slot("student", "additional-address", (complete, current) =>
    withAdditional(current, { address: complete.additionalGuardian.address }),
  ),
  slot("pickup", "student-pickup", (complete, current) =>
    withStudent(current, { pickupPeople: complete.student.pickupPeople }),
  ),
  slot("pickup", "student-walk", (complete, current) =>
    withStudent(current, { walkHome: complete.student.walkHome }),
  ),
  slot("health", "emergency1-first", (complete, current) =>
    withEmergency(current, "emergency1", { firstName: complete.emergency1.firstName }),
  ),
  slot("health", "emergency1-last", (complete, current) =>
    withEmergency(current, "emergency1", { lastName: complete.emergency1.lastName }),
  ),
  slot("health", "emergency1-phone", (complete, current) =>
    withEmergency(current, "emergency1", { phone: complete.emergency1.phone }),
  ),
  slot("health", "emergency1-relationship", (complete, current) =>
    withEmergency(current, "emergency1", { relationship: complete.emergency1.relationship }),
  ),
  slot("health", "emergency2-first", (complete, current) =>
    withEmergency(current, "emergency2", { firstName: complete.emergency2.firstName }),
  ),
  slot("health", "emergency2-last", (complete, current) =>
    withEmergency(current, "emergency2", { lastName: complete.emergency2.lastName }),
  ),
  slot("health", "emergency2-phone", (complete, current) =>
    withEmergency(current, "emergency2", { phone: complete.emergency2.phone }),
  ),
  slot("health", "emergency2-relationship", (complete, current) =>
    withEmergency(current, "emergency2", { relationship: complete.emergency2.relationship }),
  ),
  slot("health", "health-behavioral", (complete, current) =>
    withHealth(current, { behavioral: complete.health.behavioral }),
  ),
  slot(
    "health",
    "health-behavioral-detail",
    (complete, current) => withHealth(current, { behavioralDetail: complete.health.behavioralDetail }),
    (complete) => complete.health.behavioral === "yes",
  ),
  slot("health", "health-medical", (complete, current) =>
    withHealth(current, { medical: complete.health.medical }),
  ),
  slot(
    "health",
    "health-medical-detail",
    (complete, current) => withHealth(current, { medicalDetail: complete.health.medicalDetail }),
    (complete) => complete.health.medical === "yes",
  ),
  slot("health", "health-otc", (complete, current) =>
    withHealth(current, { otcMeds: complete.health.otcMeds }),
  ),
  slot("health", "health-emergency-care", (complete, current) =>
    withHealth(current, { emergencyCare: complete.health.emergencyCare }),
  ),
  slot("health", "health-physician", (complete, current) =>
    withHealth(current, { physicianName: complete.health.physicianName }),
  ),
  slot("health", "health-physician-phone", (complete, current) =>
    withHealth(current, { physicianPhone: complete.health.physicianPhone }),
  ),
  slot("health", "health-hospital", (complete, current) =>
    withHealth(current, { hospital: complete.health.hospital }),
  ),
  slot("health", "health-insurance", (complete, current) =>
    withHealth(current, { insuranceProvider: complete.health.insuranceProvider }),
  ),
  slot("health", "health-policy", (complete, current) =>
    withHealth(current, { policyNumber: complete.health.policyNumber }),
  ),
  slot("policies", "policy-conduct", (complete, current) =>
    withPolicies(current, { conduct: complete.policies.conduct }),
  ),
  slot("policies", "policy-behavior", (complete, current) =>
    withPolicies(current, { behavior: complete.policies.behavior }),
  ),
  slot("policies", "policy-dismissal", (complete, current) =>
    withPolicies(current, { dismissal: complete.policies.dismissal }),
  ),
  slot("policies", "policy-media", (complete, current) =>
    withPolicies(current, { media: complete.policies.media }),
  ),
  slot("policies", "liability-participate", (complete, current) =>
    withLiability(current, { participate: complete.liability.participate }),
  ),
  slot("policies", "liability-transport", (complete, current) =>
    withLiability(current, { transport: complete.liability.transport }),
  ),
  slot("policies", "liability-risks", (complete, current) =>
    withLiability(current, { risks: complete.liability.risks }),
  ),
  slot("policies", "liability-release", (complete, current) =>
    withLiability(current, { release: complete.liability.release }),
  ),
];

/** A permission form that passes validation, with optional guardian fields kept from the signed-in account. */
export function randomPermissionAnswers(
  options: { random?: () => number; guardian?: SampleGuardian } = {},
): PermissionAnswers {
  const random = options.random ?? Math.random;
  const given = pick(GIVEN, random);
  const family = pick(FAMILY, random);
  const programGrades = GRADE_OPTIONS.filter((item) => item.value >= 6);
  const grade = pick(programGrades, random).value;
  const tag = Math.floor(random() * 36 ** 4)
    .toString(36)
    .padStart(4, "0");
  const email = `${emailLocal(given)}.${emailLocal(family)}.${tag}@example.test`;
  const guardianGiven = keptText(options.guardian?.firstName) || pick(GIVEN, random);
  const guardianFamily = keptText(options.guardian?.lastName) || family;
  const guardianEmail =
    keptEmail(options.guardian?.email) ||
    `${emailLocal(guardianGiven)}.${emailLocal(guardianFamily)}.parent.${tag}@example.test`;
  const relationship = pick(RELATIONSHIPS, random).id;
  const otherRelationship =
    RELATIONSHIPS.find((item) => item.id !== relationship)?.id ?? relationship;
  const race = pick(RACES, random).id;
  const secondRace = RACES.find((item) => item.id !== race)?.id;
  const races: RaceId[] =
    secondRace && random() > 0.65 ? [race, secondRace] : [race];
  const behavioral = coin(random, 0.3);
  const medical = coin(random, 0.3);
  const town = pick(TOWNS, random);
  const street = `${100 + Math.floor(random() * 800)} Sample Street`;

  return {
    student: {
      firstName: given,
      lastName: family,
      email,
      grade,
      birthday: birthdayFromId(email, grade),
      phone: fakePhone(random, 11),
      address: `${street}, ${town}, MI 49682`,
      gender: pick(GENDERS, random).id as GenderId,
      races,
      school: pick(SCHOOL_FORM_ORDER, random),
      pickupPeople: `${guardianGiven} ${guardianFamily}, ${relationship}`,
      walkHome: coin(random, 0.5),
    },
    guardian: {
      firstName: guardianGiven,
      lastName: guardianFamily,
      email: guardianEmail,
      phone: keptPhone(options.guardian?.phone, random, 22),
      relationship,
      address: `${street}, ${town}, MI 49682`,
      help: [pick(HELP_INTERESTS, random).id as HelpInterestId],
    },
    additionalGuardian: {
      firstName: pick(GIVEN, random),
      lastName: guardianFamily,
      email: `second.${emailLocal(guardianFamily)}.${tag}@example.test`,
      phone: fakePhone(random, 33),
      relationship: otherRelationship,
      address: "",
    },
    emergency1: {
      firstName: pick(GIVEN, random),
      lastName: pick(FAMILY, random),
      phone: fakePhone(random, 44),
      relationship: pick(RELATIONSHIPS, random).en,
    },
    emergency2: {
      firstName: pick(GIVEN, random),
      lastName: pick(FAMILY, random),
      phone: fakePhone(random, 55),
      relationship: pick(RELATIONSHIPS, random).en,
    },
    health: {
      behavioral,
      behavioralDetail: behavioral === "yes" ? "Needs a quiet space during large groups." : "",
      medical,
      medicalDetail: medical === "yes" ? "Carries an inhaler for asthma." : "",
      otcMeds: coin(random, 0.7),
      emergencyCare: "yes",
      physicianName: "Dr. Sample",
      physicianPhone: fakePhone(random, 66),
      hospital: "Munson Medical Center",
      insuranceProvider: "Sample Health Plan",
      policyNumber: `QA-${100000 + Math.floor(random() * 899999)}`,
    },
    policies: {
      conduct: "agree",
      behavior: "agree",
      dismissal: "agree",
      media: "yes",
    },
    liability: {
      participate: true,
      transport: true,
      risks: true,
      release: true,
    },
  };
}

/** One beat per form control, in the order the permission form shows them. */
export function sampleFillBeats(complete: PermissionAnswers): SampleBeat[] {
  let current = emptyPermission();
  const beats: SampleBeat[] = [];
  for (const slot of FILL_SLOTS) {
    if (slot.when && !slot.when(complete)) continue;
    current = slot.apply(complete, current);
    beats.push({ step: slot.step, fieldId: slot.fieldId, answers: current });
  }
  return beats;
}

/** An answer for one extra review question. Empty when a choice question has no options. */
export function randomAnswerForQuestion(
  question: FormQuestion,
  random: () => number = Math.random,
): string {
  const options = question.options.map((item) => item.trim()).filter(Boolean);
  if (question.kind === "yesno") return random() < 0.5 ? "yes" : "no";
  if (question.kind === "radio" || question.kind === "dropdown") {
    return options.length > 0 ? pick(options, random) : "";
  }
  if (question.kind === "checkboxes") {
    return options.length > 0 ? joinCheckboxAnswers([pick(options, random)]) : "";
  }
  if (question.kind === "paragraph") return "Sample answer for local and QA.";
  return "Sample";
}

function slot(
  step: SampleStep,
  fieldId: string,
  apply: Slot["apply"],
  when?: Slot["when"],
): Slot {
  return { step, fieldId, apply, when };
}

function withStudent(
  current: PermissionAnswers,
  patch: Partial<PermissionAnswers["student"]>,
): PermissionAnswers {
  return { ...current, student: { ...current.student, ...patch } };
}

function withGuardian(
  current: PermissionAnswers,
  patch: Partial<PermissionAnswers["guardian"]>,
): PermissionAnswers {
  return { ...current, guardian: { ...current.guardian, ...patch } };
}

function withAdditional(
  current: PermissionAnswers,
  patch: Partial<PermissionAnswers["additionalGuardian"]>,
): PermissionAnswers {
  return {
    ...current,
    additionalGuardian: { ...current.additionalGuardian, ...patch },
  };
}

function withEmergency(
  current: PermissionAnswers,
  key: "emergency1" | "emergency2",
  patch: Partial<PermissionAnswers["emergency1"]>,
): PermissionAnswers {
  return { ...current, [key]: { ...current[key], ...patch } };
}

function withHealth(
  current: PermissionAnswers,
  patch: Partial<PermissionAnswers["health"]>,
): PermissionAnswers {
  return { ...current, health: { ...current.health, ...patch } };
}

function withPolicies(
  current: PermissionAnswers,
  patch: Partial<PermissionAnswers["policies"]>,
): PermissionAnswers {
  return { ...current, policies: { ...current.policies, ...patch } };
}

function withLiability(
  current: PermissionAnswers,
  patch: Partial<PermissionAnswers["liability"]>,
): PermissionAnswers {
  return { ...current, liability: { ...current.liability, ...patch } };
}

function pick<T>(items: readonly T[], random: () => number): T {
  return items[Math.floor(random() * items.length)] as T;
}

function coin(random: () => number, yesBelow: number): YesNo {
  return random() < yesBelow ? "yes" : "no";
}

function emailLocal(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 32) || "family";
}

function keptText(value: string | undefined): string {
  return value?.trim() ?? "";
}

function keptEmail(value: string | undefined): string {
  const email = value?.trim().toLowerCase() ?? "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
}

function fakePhone(random: () => number, salt: number): string {
  const last = String((Math.floor(random() * 10000) + salt) % 10000).padStart(4, "0");
  return formatPhoneInput(`231555${last}`);
}

function keptPhone(value: string | undefined, random: () => number, salt: number): string {
  const formatted = value?.trim() ? formatPhoneInput(value) : "";
  return phoneDigits(formatted).length === 10 ? formatted : fakePhone(random, salt);
}
